import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, MouseEvent, ReactNode } from 'react';
import { Children, memo, useEffect, useRef } from 'react';

import { theme } from '../../theme/cobrainer-theme';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../dropdown-menu/DropdownMenu';
import { IconChevronRightSmall, IconThreeDots, SoftSkillIcon } from '../icons';
import { RecessedSurface } from '../recessed-surface/RecessedSurface';
import { recessedSurfaceClassNames } from '../surface/surfaceClasses';
import { Card, CardButton } from './Card';

type CardWithContextMenuProps = {
    readonly id: string;
    readonly canGenerateWithAI?: boolean;
    readonly children?: ReactNode;
    readonly contextMenuOptions?: ReactNode;
    readonly counts?: {
        readonly levels?: number;
        readonly positions?: number;
    };
    readonly hasChildren?: boolean;
    readonly isActionPending?: boolean;
    readonly isActive?: boolean;
    readonly isCollapsed?: boolean;
    readonly isCopied?: boolean;
    readonly isCut?: boolean;
    readonly isGenerating?: boolean;
    readonly isSelected: boolean;
    readonly onDoubleClick?: (id: string, e?: MouseEvent<HTMLDivElement>) => void;
    readonly onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    readonly onSelect: (id: string, e: MouseEvent<HTMLDivElement>) => void;
    readonly onToggleCollapse?: (id: string) => void;
    readonly reviewerName?: string;
    readonly selectedSurface?: 'default' | 'recessed';
    readonly status?: 'published' | 'ready_to_publish' | 'unverified';
    readonly subtitle?: ReactNode;
    readonly suffix?: ReactNode;
    readonly suffixAlignment?: 'center' | 'start';
    readonly title: string;
};

const preventDoubleClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
};

const CollapseToggleButton: FunctionComponent<{
    readonly isActive: boolean;
    readonly isCollapsed: boolean;
    readonly isSelected: boolean;
    readonly onToggleCollapse: (e: MouseEvent<HTMLButtonElement>) => void;
}> = ({ isActive, isCollapsed, isSelected, onToggleCollapse }) => (
    <CardButton
        iconOnly={true}
        isActive={isActive}
        isSelected={isSelected}
        style={{
            marginLeft: `-${theme.spacing.xl}`,
            marginRight: `-${theme.spacing.l}`,
        }}
        title={isCollapsed ? 'Expand' : 'Collapse'}
        variant="link"
        onClick={onToggleCollapse}
        onDoubleClick={preventDoubleClick}
    >
        <IconChevronRightSmall
            className={clsx(isActive ? 'text-white-200' : 'text-gray-800')}
            style={{
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                transition: 'transform 0.2s ease-in-out',
            }}
        />
    </CardButton>
);

const GeneratingIndicator: FunctionComponent = () => {
    return (
        <span
            className="text-orange-500 ml-xs inline-flex"
            data-testid="generating-indicator"
            style={{ animation: 'cardPulse 1s ease-in-out infinite' }}
        >
            <SoftSkillIcon />
        </span>
    );
};

const ContextMenuDropdown: FunctionComponent<{
    readonly contextMenuOptions: ReactNode;
    readonly isActive: boolean;
    readonly isSelected: boolean;
}> = ({ contextMenuOptions, isActive, isSelected }) => (
    // biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration
    // biome-ignore lint/a11y/useKeyWithClickEvents: suppressed due to migration
    <div
        className="opacity-100 transition-opacity duration-200"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.stopPropagation()}
    >
        <DropdownMenu>
            <DropdownMenuTrigger>
                <CardButton
                    data-show-on-hover={true}
                    data-testid="three-dot-menu"
                    iconOnly={true}
                    isActive={isActive}
                    isSelected={isSelected}
                    size="sm"
                    title="More options"
                    variant="link"
                >
                    <IconThreeDots size={16} />
                </CardButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">{contextMenuOptions}</DropdownMenuContent>
        </DropdownMenu>
    </div>
);

const CardContent: FunctionComponent<{
    readonly contextMenu: ReactNode;
    readonly isActive: boolean;
    readonly isCollapsed: boolean;
    readonly isGenerating: boolean;
    readonly isSelected: boolean;
    readonly selectedSurface: 'default' | 'recessed';
    readonly onToggleCollapse: (e: MouseEvent<HTMLButtonElement>) => void;
    readonly shouldShowToggleButton: boolean;
    readonly subtitle?: ReactNode;
    readonly suffix: ReactNode;
    readonly suffixAlignment: 'center' | 'start';
    readonly title: string;
}> = ({
    contextMenu,
    isActive,
    isCollapsed,
    isGenerating,
    isSelected,
    selectedSurface,
    onToggleCollapse,
    shouldShowToggleButton,
    subtitle,
    suffix,
    suffixAlignment,
    title,
}) => {
    const hasContextMenu = contextMenu !== null;
    const hasSuffixContent = suffix !== null || hasContextMenu;
    const titleColumnClass = shouldShowToggleButton ? 'col-start-2' : 'col-start-1';
    const suffixColumnClass = shouldShowToggleButton ? 'col-start-3' : 'col-start-2';

    return (
        <div
            className={clsx(
                'grid flex-1 gap-x-s gap-y-0 justify-start min-h-[40px] relative w-full',
                shouldShowToggleButton && hasSuffixContent && 'grid-cols-[auto_minmax(0,1fr)_auto]',
                shouldShowToggleButton && !hasSuffixContent && 'grid-cols-[auto_minmax(0,1fr)]',
                !shouldShowToggleButton && hasSuffixContent && 'grid-cols-[minmax(0,1fr)_auto]',
                !shouldShowToggleButton && !hasSuffixContent && 'grid-cols-[minmax(0,1fr)]',
                isActive ? 'text-white-200' : 'text-black-700',
            )}
        >
            {shouldShowToggleButton && (
                <div className="relative col-start-1 row-start-1 h-4xl w-[calc(var(--spacing-7xl)-var(--spacing-xl)-var(--spacing-l))] self-start [&_button]:text-current">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2">
                        <CollapseToggleButton
                            isActive={isActive}
                            isCollapsed={isCollapsed}
                            isSelected={isSelected}
                            onToggleCollapse={onToggleCollapse}
                        />
                    </div>
                </div>
            )}
            <div
                className={clsx(
                    'row-start-1 flex min-h-4xl items-center self-start text-sm font-medium leading-[1.25rem] tracking-tight select-none whitespace-normal break-words min-w-0',
                    titleColumnClass,
                    selectedSurface === 'recessed' && isSelected && !isActive && 'text-black-900',
                )}
            >
                {title}
                {isGenerating && <GeneratingIndicator />}
            </div>
            {hasSuffixContent && (
                <div
                    className={clsx(
                        'relative row-start-1 flex shrink-0 items-center ml-xl',
                        suffixColumnClass,
                        suffixAlignment === 'center' && 'row-span-2 self-center',
                        suffixAlignment === 'start' && 'self-start',
                        suffixAlignment === 'start' &&
                            (suffix === null && hasContextMenu ? 'min-h-[26px] min-w-6xl -mt-[3px]' : '-mt-xxs'),
                        suffixAlignment === 'center' &&
                            suffix === null &&
                            hasContextMenu &&
                            'min-h-[32px] min-w-[32px]',
                    )}
                >
                    {hasContextMenu && (
                        <div
                            className={clsx(
                                'absolute top-1/2 z-10 -translate-y-1/2 [&_button]:text-current',
                                suffix === null ? 'right-0' : 'right-[calc(100%+var(--spacing-m))]',
                            )}
                        >
                            {contextMenu}
                        </div>
                    )}
                    {suffix}
                </div>
            )}
            {subtitle !== undefined && (
                <div
                    className={clsx(
                        'row-start-2 flex min-h-[26px] items-center text-xs font-normal leading-[1.125rem] tracking-tight select-none min-w-0',
                        titleColumnClass,
                        isActive ? 'text-white-200' : 'text-gray-700',
                    )}
                >
                    {subtitle}
                </div>
            )}
        </div>
    );
};

export const CardWithContextMenu: FunctionComponent<CardWithContextMenuProps> = memo(
    ({
        id,
        children,
        contextMenuOptions,
        isActionPending = false,
        isActive = false,
        isCollapsed = true,
        isCopied = false,
        isCut = false,
        isGenerating = false,
        isSelected,
        onDoubleClick,
        onKeyDown,
        onSelect,
        onToggleCollapse,
        selectedSurface = 'default',
        subtitle = undefined,
        suffix = null,
        suffixAlignment = 'start',
        title,
    }) => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (isActive) {
                ref.current?.focus();
            }
        }, [isActive]);

        const hasChildren = Children.count(children) > 0;

        const hasContextMenu = Children.count(contextMenuOptions) > 0;
        const shouldShowToggleButton = hasChildren && onToggleCollapse !== undefined;
        const shouldShowChildren = hasChildren && !isCollapsed;
        const useRecessedSurface = selectedSurface === 'recessed';
        const showRecessedSurface = useRecessedSurface && isSelected;
        const enableHoverRecessedSurface = useRecessedSurface && !isSelected && !isActionPending;

        const handleSelect = (e: MouseEvent<HTMLDivElement>): void => {
            if (isActionPending) return;
            onSelect(id, e);
            ref.current?.focus();
        };
        const handleToggleCollapse = (e: MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            if (onToggleCollapse !== undefined) {
                onToggleCollapse(id);
            }
        };
        const handleDoubleClick = (e: MouseEvent<HTMLDivElement>): void => {
            if (isActionPending) return;
            onDoubleClick?.(id, e);
        };
        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onDoubleClick?.(id);
            } else {
                onKeyDown?.(e);
            }
        };

        const contextMenu =
            isActionPending || !hasContextMenu ? null : (
                <ContextMenuDropdown
                    contextMenuOptions={contextMenuOptions}
                    isActive={isActive}
                    isSelected={isSelected}
                />
            );

        const childrenWrapper = !shouldShowChildren ? null : (
            <motion.div
                animate={{ height: 'auto', opacity: 1, overflowY: 'visible', scale: 1 }}
                className="flex flex-col gap-xs justify-self-end mt-xs"
                exit={{ height: 0, opacity: 0.6, overflowY: 'hidden', scale: 0.95 }}
                initial={{ height: 0, opacity: 0.6, overflowY: 'hidden', scale: 0.95 }}
                style={{
                    marginLeft: theme.spacing['8xl'],
                    width: `calc(100% - ${theme.spacing['8xl']})`,
                }}
            >
                {children}
            </motion.div>
        );

        const card = (
            <Card
                ref={ref}
                className={
                    useRecessedSurface
                        ? clsx(
                              'w-full !rounded-recessed-pocket !p-0 !shadow-none',
                              '!bg-transparent',
                              showRecessedSurface && '!outline-none',
                          )
                        : undefined
                }
                isActionPending={isActionPending}
                isActive={isActive}
                isCopied={isCopied}
                isCut={isCut}
                isSelected={isSelected}
                tabIndex={isActive ? 0 : -1}
                onClick={handleSelect}
                onDoubleClick={handleDoubleClick}
                onKeyDown={handleKeyDown}
            >
                {useRecessedSurface ? (
                    <RecessedSurface
                        active={showRecessedSurface}
                        hoverActive={enableHoverRecessedSurface}
                        hoverPocketTone="neutral"
                        hoverSurfaceShadow="sm"
                        hoverSurfaceTone="gray"
                        inactiveBehavior="preserve-structure"
                        inactiveSurfaceShadow="sm"
                        inactiveSurfaceTone="white"
                        placement="inside"
                        pocketTone={!isActive ? 'orange' : 'neutral'}
                        surfaceClassName={clsx(
                            recessedSurfaceClassNames.radius.inner,
                            'px-[calc(var(--spacing-xl)-var(--spacing-xxs))] py-[calc(var(--spacing-xl)-var(--spacing-xxs))]',
                        )}
                        surfaceShadow={isActive ? 'orange-sm' : 'sm'}
                        surfaceTone={isActive ? 'orange' : 'gray'}
                        transition="none"
                    >
                        <CardContent
                            contextMenu={contextMenu}
                            isActive={isActive}
                            isCollapsed={isCollapsed}
                            isGenerating={isGenerating}
                            isSelected={isSelected}
                            selectedSurface={selectedSurface}
                            shouldShowToggleButton={shouldShowToggleButton}
                            subtitle={subtitle}
                            suffix={suffix}
                            suffixAlignment={suffixAlignment}
                            title={title}
                            onToggleCollapse={handleToggleCollapse}
                        />
                    </RecessedSurface>
                ) : (
                    <CardContent
                        contextMenu={contextMenu}
                        isActive={isActive}
                        isCollapsed={isCollapsed}
                        isGenerating={isGenerating}
                        isSelected={isSelected}
                        selectedSurface={selectedSurface}
                        shouldShowToggleButton={shouldShowToggleButton}
                        subtitle={subtitle}
                        suffix={suffix}
                        suffixAlignment={suffixAlignment}
                        title={title}
                        onToggleCollapse={handleToggleCollapse}
                    />
                )}
            </Card>
        );

        return (
            <motion.div
                className={clsx(
                    'flex-1 outline-none w-full',
                    '[&_[data-show-on-hover=true]]:opacity-0',
                    '[&_[data-state=open]]:bg-transparent [&_[data-state=open]]:opacity-100',
                    '[&:hover_[data-show-on-hover=true]]:opacity-100',
                )}
                role="option"
            >
                {card}
                <AnimatePresence mode="sync">{childrenWrapper}</AnimatePresence>
            </motion.div>
        );
    },
);
